import { breakpoints, Inline, Text, vars } from '@etg/wings';
import type { Route, SearchData } from '@eti/providers';
import { DIRECTIONS, useDateFormat, useDirection, useSearch, useSiteContext } from '@eti/providers';
import { TripType } from '@eti/schema-types';
import { css } from '@eti/styles';
import { constants, date as dateUtil, useScrollTo } from '@eti/utils';
import {
  ArrowLeft,
  ArrowRight,
  ArrowsLeftRight as ReturnTripArrow,
  CalendarBlank,
  CaretDown,
  User,
} from '@phosphor-icons/react';
import { Fragment } from 'react';

const titleStyles = css`
  margin: auto 0;
  position: relative;

  @media (min-width: ${breakpoints._768}) {
    align-items: center;
  }
`;

const titleTextStyles = css`
  font-size: 0.875rem;
  font-weight: 700;

  @media (max-width: ${breakpoints._560}) {
    min-width: 80%;
  }
`;

const separatorStyles = css`
  font-size: 0.875rem;
  padding: 0 0.2rem;

  @media (max-width: ${breakpoints._560}) {
    font-size: 0.75rem;
  }
`;

const dividerStyles = css`
  background: ${vars.colors.divider};
  height: 21px;
  width: 1px;

  @media (max-width: ${breakpoints._560}) {
    display: none;
  }
`;

const detailsStyles = css`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const detailsItemStyles = css`
  align-items: center;
  display: flex;
  gap: 4px;
  height: 16px;
`;

const getTriggerStyles = (isSearchComplete: boolean) => css`
  background: ${vars.colors.surface.primary};
  border: 1px solid ${vars.colors.divider};
  border-radius: ${vars.card.borderRadius};
  cursor: ${isSearchComplete ? 'pointer' : 'not-allowed'};
  padding: 8px 16px;
  -webkit-tap-highlight-color: transparent;
  width: 100%;

  @media (min-width: ${breakpoints._560}) {
    width: fit-content;
  }

  @media (min-width: ${breakpoints._768}) {
    max-width: 450px;
  }

  @media (min-width: ${breakpoints._970}) {
    max-width: 665px;
  }

  @media (min-width: 1170px) {
    max-width: 785px;
  }

  &:focus-visible {
    outline: 2px solid ${vars.colors.header.content};
    outline-offset: 2px;
  }
`;

const tripDateStyles = css`
  align-items: center;
  display: flex;
`;

const multiStopContainerStyles = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  span {
    + svg {
      margin: -1.2px 8px;
    }
  }

  @media (max-width: ${breakpoints._560}) {
    padding-right: 40px;
  }

  @media (min-width: ${breakpoints._560}) {
    max-width: 114px;
  }

  @media (min-width: ${breakpoints._768}) {
    max-width: 96px;
  }

  @media (min-width: ${breakpoints._970}) {
    max-width: 275px;
  }
`;

const tripLocationStyles = css`
  line-height: 20px;

  span {
    &.originCity,
    &.destinationCity {
      display: none;

      @media (min-width: ${breakpoints._970}) {
        display: initial;
        max-width: 70px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      @media (min-width: 1170px) {
        max-width: 150px;
      }
    }

    &:nth-of-type(2) {
      @media (min-width: 970px) {
        &::before {
          content: '(';
        }

        &::after {
          content: ')';
        }
      }
    }
  }
`;

const getFauxButtonStyles = (isMyTrip: boolean) => css`
  align-items: center;
  border-radius: ${isMyTrip ? '0.5rem' : vars.card.borderRadius};
  display: flex;
  height: 2rem;
  justify-content: center;
  position: absolute;
  right: 0;
  top: 0.25rem;
  width: 2rem;

  @media (min-width: ${breakpoints._560}) {
    background-color: ${vars.colors.button.primary.default.base};
    position: static;
    width: 2.125rem;
  }
`;

const getCaretStyles = (isOpen?: boolean) => css`
  color: ${vars.colors.text.primary};
  height: 1.25rem;
  transform: rotateX(${isOpen ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
  width: 1.25rem;

  @media (min-width: ${breakpoints._560}) {
    color: ${vars.colors.button.primary.default.label};
  }
`;

const timeTextStyle = css`
  @media (max-width: ${breakpoints._560}) {
    font-size: 0.75rem;
  }
`;

const getMultiCityRoutes = (routes?: Route[]): string[] => {
  if (!routes || routes.length === 0) {
    return [];
  }

  return routes.flatMap((route, index) => {
    const nextRoute = routes[index + 1];
    const codes: string[] = [];

    if (route.originCityCode) {
      codes.push(route.originCityCode);
    }

    if (
      (!nextRoute || route.destinationCity !== nextRoute.originCity) &&
      route.destinationCityCode
    ) {
      codes.push(route.destinationCityCode);
    }

    return codes;
  });
};

const getTripTypeIcon = (direction: 'ltr' | 'rtl', tripType?: TripType) => {
  if (tripType === TripType.Return) {
    return ReturnTripArrow;
  }

  if (tripType === TripType.OneWay) {
    return direction === DIRECTIONS.LTR ? ArrowRight : ArrowLeft;
  }

  return ArrowRight;
};

const TripDetailsTitleWrapper = () => {
  const { brand } = useSiteContext();
  const { currentSearchData, isSearchComplete, isSearchFormOpen, setIsSearchFormOpen } =
    useSearch();
  const { scrollVertical } = useScrollTo();
  const { day } = useDateFormat();
  const { direction } = useDirection();

  const {
    date,
    destination,
    origin,
    travelers,
    tripType,
    routes = [],
  } = currentSearchData as SearchData;
  const numberOfTravelers = travelers && travelers?.adults + travelers?.childAges.length;
  const TripTypeIcon = getTripTypeIcon(direction, tripType);
  const { MYTRIP } = constants.brands;
  const isMyTripBrand = brand.code === MYTRIP;

  const handleClick = () => {
    const nextState = !isSearchFormOpen;

    setIsSearchFormOpen?.(nextState);

    if (nextState) {
      scrollVertical('smooth', 0);
    }
  };

  const multiCityRoutes = getMultiCityRoutes(routes);
  const {
    destinationCity,
    destinationCityCode,
    destinationCode,
    originCityCode,
    originCode,
    originCity,
  } = routes[0];
  const destinationCityName = destinationCity || destination?.city;
  const originCityName = originCity || origin?.city;

  const tripRoutes = (() => {
    switch (tripType) {
      case TripType.MultiStop:
      case TripType.OpenJawSingleOrigin:
      case TripType.OpenJawDouble:
      case TripType.OpenJawSingleDestination:
        return (
          <div className={multiStopContainerStyles}>
            {multiCityRoutes.map((city, index) => (
              <Fragment key={`${city}-${index}`}>
                <span data-testid={`city-${index}-name`}>{city}</span>
                {index !== multiCityRoutes.length - 1 && (
                  <TripTypeIcon
                    aria-hidden
                    data-testid={`${tripType}-icon`}
                    size={12}
                    weight="fill"
                  />
                )}
              </Fragment>
            ))}
          </div>
        );
      case TripType.OneWay:
      case TripType.Return:
        return (
          <>
            <Inline className={tripLocationStyles} spacing={4}>
              <span className="originCity" data-testid="origin-city-name">
                {originCityName}
              </span>
              <span data-testid="origin-city-code">{originCityCode || originCode}</span>
            </Inline>
            <TripTypeIcon aria-hidden data-testid={`${tripType}-icon`} weight="fill" />
            <Inline className={tripLocationStyles} spacing={4}>
              <span className="destinationCity" data-testid="destination-city-name">
                {destinationCityName}
              </span>
              <span data-testid="destination-city-code">
                {destinationCityCode || destinationCode}
              </span>
            </Inline>
          </>
        );
      default:
        return null;
    }
  })();

  return (
    <button
      aria-controls="result-search-details-section"
      aria-expanded={isSearchFormOpen}
      className={getTriggerStyles(Boolean(isSearchComplete))}
      data-testid="trip-details-title-wrapper"
      disabled={!isSearchComplete}
      onClick={handleClick}
      type="button"
    >
      <Inline
        className={titleStyles}
        spacing={{
          [breakpoints._0]: 4,
          [breakpoints._560]: 16,
        }}
      >
        <Inline
          alignY="center"
          className={titleTextStyles}
          data-testid="tripDetails-title-TitleText"
          spacing={8}
        >
          {tripRoutes}
        </Inline>
        <div className={detailsStyles}>
          <div className={dividerStyles} />
          <div className={detailsItemStyles}>
            <CalendarBlank aria-hidden />
            <div className={tripDateStyles} data-testid="tripDetails-title-date-desktop">
              <Text className={timeTextStyle} variant="14">
                <time dateTime={date?.departure}>{dateUtil.format(date?.departure, day)}</time>
              </Text>
              {tripType !== TripType.OneWay && (
                <>
                  <span
                    className={separatorStyles}
                    data-testid="tripDetails-title-separator-desktop"
                  >
                    –
                  </span>
                  <Text className={timeTextStyle} variant="14">
                    <time dateTime={date?.return}>{dateUtil.format(date?.return, day)}</time>
                  </Text>
                </>
              )}
            </div>
          </div>
          <div className={dividerStyles} />
          <div className={detailsItemStyles}>
            <User aria-hidden size={16} />
            <Text
              className={timeTextStyle}
              data-testid="tripDetails-number-of-travelers"
              variant="14"
            >
              {numberOfTravelers}
            </Text>
          </div>
          <span className={getFauxButtonStyles(isMyTripBrand)} data-testid="toggleButton">
            <CaretDown
              aria-hidden
              className={getCaretStyles(isSearchFormOpen)}
              data-testid="caret-icon"
              weight="bold"
            />
          </span>
        </div>
      </Inline>
    </button>
  );
};

export default TripDetailsTitleWrapper;
